import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Check, X, Coins } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

const EMPTY_PLAN = {
  name: '', price: '', credits: '', stripe_price_id: '',
  description: '', features: '', duration_days: '30',
  is_visible: true, is_active: true, sort_order: '0'
};

export default function AdminPlanManager() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_PLAN });

  useEffect(() => { fetchPlans(); }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/subscription-plans`, { withCredentials: true });
      setPlans(response.data);
    } catch { console.error('Failed to fetch plans'); }
    finally { setLoading(false); }
  };

  const toPlanPayload = (data) => ({
    name: data.name,
    price: parseFloat(data.price),
    credits: parseInt(data.credits) || 0,
    stripe_price_id: data.stripe_price_id || null,
    description: data.description || '',
    features: data.features ? data.features.split('\n').filter(f => f.trim()) : [],
    duration_days: parseInt(data.duration_days) || 30,
    is_visible: data.is_visible,
    is_active: data.is_active,
    sort_order: parseInt(data.sort_order) || 0
  });

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/admin/subscription-plans`, toPlanPayload(formData), { withCredentials: true });
      toast.success('Plan created!');
      setFormData({ ...EMPTY_PLAN });
      setShowForm(false);
      fetchPlans();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add plan'); }
  };

  const handleEdit = (plan) => {
    setEditing(plan.plan_id);
    setFormData({
      name: plan.name || '', price: (plan.price ?? 0).toString(),
      credits: (plan.credits ?? plan.chat_allowance ?? 0).toString(),
      stripe_price_id: plan.stripe_price_id || '',
      description: plan.description || '',
      features: Array.isArray(plan.features) ? plan.features.join('\n') : '',
      duration_days: (plan.duration_days ?? 30).toString(),
      is_visible: plan.is_visible !== false, is_active: plan.is_active !== false,
      sort_order: (plan.sort_order ?? 0).toString()
    });
  };

  const handleSave = async (planId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/subscription-plans/${planId}`, toPlanPayload(formData), { withCredentials: true });
      toast.success('Plan updated!');
      setEditing(null);
      setFormData({ ...EMPTY_PLAN });
      fetchPlans();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update plan'); }
  };

  const handleDelete = async (planId, planName) => {
    if (!confirm(`Delete "${planName}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/subscription-plans/${planId}`, { withCredentials: true });
      toast.success('Plan deleted!');
      fetchPlans();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete plan'); }
  };

  const f = (field, val) => setFormData({ ...formData, [field]: val });

  if (loading) return <div className="animate-pulse h-32 bg-slate-100 rounded-xl" />;

  return (
    <div className="card" data-testid="plans-section">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-bold text-slate-900">Subscription Plans</h3>
        <button onClick={() => setShowForm(!showForm)} className="btn-accent text-sm flex items-center gap-2" data-testid="add-plan-btn">
          <Plus className="h-4 w-4" /> Add Plan
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleAdd} className="space-y-3 mb-4 pb-4 border-b border-slate-200" data-testid="plan-form">
          <div className="grid grid-cols-2 gap-3">
            <input type="text" value={formData.name} onChange={(e) => f('name', e.target.value)} placeholder="Plan name" required className="w-full" data-testid="plan-name-input" />
            <input type="number" value={formData.price} onChange={(e) => f('price', e.target.value)} placeholder="Monthly price (£)" required step="0.01" className="w-full" data-testid="plan-price-input" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <input type="number" value={formData.credits} onChange={(e) => f('credits', e.target.value)} placeholder="Credits included" required className="w-full" data-testid="plan-credits-input" />
            <input type="number" value={formData.duration_days} onChange={(e) => f('duration_days', e.target.value)} placeholder="Duration (days)" className="w-full" data-testid="plan-duration-input" />
            <input type="number" value={formData.sort_order} onChange={(e) => f('sort_order', e.target.value)} placeholder="Sort order" className="w-full" data-testid="plan-sort-input" />
          </div>
          <input type="text" value={formData.stripe_price_id} onChange={(e) => f('stripe_price_id', e.target.value)} placeholder="Stripe Price ID (price_xxx)" className="w-full font-mono text-sm" data-testid="plan-stripe-price-input" />
          <textarea value={formData.description} onChange={(e) => f('description', e.target.value)} placeholder="Description" rows={2} className="w-full" data-testid="plan-description-input" />
          <textarea value={formData.features} onChange={(e) => f('features', e.target.value)} placeholder="Features (one per line)" rows={3} className="w-full text-sm" data-testid="plan-features-input" />
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={formData.is_visible} onChange={(e) => f('is_visible', e.target.checked)} className="rounded" />
              <span className="text-sm text-slate-700">Visible</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={formData.is_active} onChange={(e) => f('is_active', e.target.checked)} className="rounded" />
              <span className="text-sm text-slate-700">Active</span>
            </label>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="btn-primary text-sm" data-testid="submit-plan-btn">Create Plan</button>
            <button type="button" onClick={() => setShowForm(false)} className="btn-secondary text-sm" data-testid="cancel-plan-btn">Cancel</button>
          </div>
        </form>
      )}

      <div className="space-y-3" data-testid="plans-list">
        {plans.map((plan) => (
          <div key={plan.plan_id} className={`p-4 rounded-lg border ${plan.is_active !== false ? 'bg-slate-50 border-slate-200' : 'bg-red-50 border-red-200'}`} data-testid={`plan-${plan.plan_id}`}>
            {editing === plan.plan_id ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <input type="text" value={formData.name} onChange={(e) => f('name', e.target.value)} className="w-full text-sm" data-testid="edit-plan-name-input" />
                  <input type="number" value={formData.price} onChange={(e) => f('price', e.target.value)} step="0.01" className="w-full text-sm" data-testid="edit-plan-price-input" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <input type="number" value={formData.credits} onChange={(e) => f('credits', e.target.value)} className="w-full text-sm" data-testid="edit-plan-credits-input" />
                  <input type="number" value={formData.duration_days} onChange={(e) => f('duration_days', e.target.value)} className="w-full text-sm" data-testid="edit-plan-duration-input" />
                  <input type="number" value={formData.sort_order} onChange={(e) => f('sort_order', e.target.value)} className="w-full text-sm" data-testid="edit-plan-sort-input" />
                </div>
                <input type="text" value={formData.stripe_price_id} onChange={(e) => f('stripe_price_id', e.target.value)} placeholder="Stripe Price ID" className="w-full text-sm font-mono" data-testid="edit-plan-stripe-input" />
                <textarea value={formData.description} onChange={(e) => f('description', e.target.value)} placeholder="Description" rows={2} className="w-full text-sm" data-testid="edit-plan-desc-input" />
                <textarea value={formData.features} onChange={(e) => f('features', e.target.value)} placeholder="Features (one per line)" rows={3} className="w-full text-sm" data-testid="edit-plan-features-input" />
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.is_visible} onChange={(e) => f('is_visible', e.target.checked)} className="rounded" />
                    <span className="text-sm text-slate-700">Visible</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.is_active} onChange={(e) => f('is_active', e.target.checked)} className="rounded" />
                    <span className="text-sm text-slate-700">Active</span>
                  </label>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleSave(plan.plan_id)} className="text-green-600 hover:text-green-700 p-1" data-testid="save-plan-btn"><Check className="h-4 w-4" /></button>
                  <button onClick={() => { setEditing(null); setFormData({ ...EMPTY_PLAN }); }} className="text-slate-600 hover:text-slate-700 p-1" data-testid="cancel-edit-plan-btn"><X className="h-4 w-4" /></button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-slate-900">{plan.name}</p>
                    {plan.is_active === false && <span className="px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded">Inactive</span>}
                    {plan.is_visible === false && <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">Hidden</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-lg font-bold text-orange-500">£{plan.price ?? 0}</p>
                    <div className="flex gap-1">
                      <button type="button" onClick={(e) => { e.stopPropagation(); handleEdit(plan); }} className="text-blue-600 hover:text-blue-700 p-1" data-testid={`edit-plan-btn-${plan.plan_id}`}><Edit className="h-4 w-4" /></button>
                      <button type="button" onClick={(e) => { e.stopPropagation(); handleDelete(plan.plan_id, plan.name); }} className="text-red-600 hover:text-red-700 p-1" data-testid={`delete-plan-btn-${plan.plan_id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 text-sm text-slate-600 mb-2">
                  <span className="flex items-center gap-1"><Coins className="h-3 w-3 text-orange-500" /> {plan.credits ?? plan.chat_allowance ?? 0} credits</span>
                  <span>{plan.duration_days ?? 30} days</span>
                  {plan.stripe_price_id && <span className="font-mono text-xs text-slate-400">{plan.stripe_price_id}</span>}
                </div>
                {plan.description && <p className="text-sm text-slate-600">{plan.description}</p>}
                {plan.features && plan.features.length > 0 && (
                  <ul className="mt-2 text-xs text-slate-500 space-y-0.5">
                    {plan.features.slice(0, 3).map((feat) => <li key={feat}>{'\u2022'} {feat}</li>)}
                    {plan.features.length > 3 && <li className="text-slate-400">+{plan.features.length - 3} more</li>}
                  </ul>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
